import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Button, Col, Modal, Row } from "react-bootstrap";
import fileDownload from "js-file-download";

// Components
import DropZone from "../../global/DropZone";
import TextLoading from "../../global/TextLoading";

// Services
import { GetScoreCsvTemplate, ImportClassScores } from "../../../api/class";

const ModalImportScores = ({ show, onHide, classId, periodId }) => {
  const { t } = useTranslation(["class", "global"]);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [file, setFile] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const getFile = (file = []) => {
    if (file?.length) {
      setFile(file);
    }
  };

  const getTemplate = () => {
    const payload = {
      class_id: classId,
    };
    if (periodId) payload.grading_period_id = periodId;
    setIsDownloadingTemplate(true);
    GetScoreCsvTemplate(payload)
      .then((result) => {
        if (result.status === 200) {
          fileDownload(result.data, `class_${classId}_score_csv_template.csv`);
        }
      })
      .finally(() => {
        setIsDownloadingTemplate(false);
      });
  };

  const importScores = () => {
    const payload = new FormData();
    payload.append("class_id", classId);
    payload.append("class_score_template_csv", file[0]);
    if (periodId) payload.append("grading_period_id", periodId);

    setIsSaving(true);
    ImportClassScores(payload)
      .then((result) => {
        if (result) {
          onHide();
        }
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <React.Fragment>
      <Modal show={show} id="modalImportScores">
        <Modal.Header>
          <Modal.Title id="importScoresTitle">
            {t("class:modalImportScores.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Col xs={6} id="importScoresDescription">
              {t("class:modalImportScores.description")}
            </Col>
            <Col xs={6} className="text-end" id="downloadTemplateContainer">
              <Button
                id="downloadTemplateBtn"
                variant="primary"
                onClick={getTemplate}
                disabled={isDownloadingTemplate || isSaving}
              >
                {isDownloadingTemplate ? (
                  <TextLoading
                    variant="light"
                    text={t("global:buttons.downloading")}
                  />
                ) : (
                  t("global:importCSV.downloadTemplate")
                )}
              </Button>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col xs={12} className="fw-bold" id="importScoresNote">
              {t("class:modalImportScores.note")}
            </Col>
          </Row>
          <Row className="mb-3">
            <Col xs={12} className="mt-3" id="dropZoneImportScoresContainer">
              <DropZone
                getFile={getFile}
                accept=".csv, .xlsx, .xls"
                noClick
                noKeyboard
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            id="cancelImportScoresBtn"
            variant="secondary"
            onClick={onHide}
            disabled={isSaving}
          >
            {t("global:buttons.cancel")}
          </Button>
          <Button
            id="importScoresBtn"
            variant="primary"
            onClick={importScores}
            disabled={isSaving || !file?.length}
          >
            {isSaving ? (
              <TextLoading
                variant="light"
                text={t("global:buttons.importing")}
              />
            ) : (
              t("global:buttons.import")
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

ModalImportScores.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  classId: PropTypes.string,
  periodId: PropTypes.string,
};

export default ModalImportScores;
